/* eslint-disable no-console -- It's our fallback for logrocket */
import * as Sentry from '@sentry/react';

/**
 * Unified Logger with Sentry Integration
 *
 * Clean architecture with clear separation:
 * - Breadcrumbs: Context trail (no events, cost-free)
 * - Events: Warnings and errors (creates Sentry events)
 * - User tracking: Associate errors with users
 *
 * GDPR Compliance:
 * - Errors/warnings sent to Sentry (legitimate interest)
 * - Breadcrumbs attached to errors only
 * - PII stripped by Sentry's beforeSend hook
 */

// ============================================================================
// Types
// ============================================================================

export type LogContext = Record<string, unknown> | Error;
export type UserContext = {
  id?: string | number;
  email?: string;
  username?: string;
  [key: string]: unknown;
};
export type SentryLevel = 'debug' | 'info' | 'log' | 'warning' | 'error';

// ============================================================================
// Constants
// ============================================================================

const EXCEPTION_CONTEXT_KEY = 'exception_context';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize message to string for Sentry
 */
const normalizeMessage = (message: string | Error): string => typeof message === 'string' ? message : message.toString();

/**
 * Normalize context to a plain object for Sentry compatibility.
 * Converts Error objects to a serializable format.
 */
const normalizeContext = (context: LogContext | undefined): Record<string, unknown> | undefined => {
  if (!context) return undefined;
  if (context instanceof Error) {
    return {
      name: context.name,
      message: context.message,
      stack: context.stack
    };
  }
  return context;
};

/**
 * Add breadcrumb to Sentry (DRY helper)
 */
const addSentryBreadcrumb = (message: string, category: string, level: SentryLevel, data?: LogContext): void => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data: normalizeContext(data),
    timestamp: Date.now() / 1000
  });
};

/**
 * Log to console in development (DRY helper)
 */
const consoleLog = (level: 'log' | 'info' | 'debug' | 'warn' | 'error', message: string | Error, data?: LogContext): void => {
  if (IS_DEVELOPMENT) {
    console[level](message, data);
  }
};

// ============================================================================
// Core Logging Functions (Breadcrumbs - No Sentry Events)
// ============================================================================

/**
 * Log - General logging
 * Adds breadcrumb to Sentry (no event, just context)
 */
const log = (message: string | Error, context?: LogContext): void => {
  const msg = normalizeMessage(message);
  addSentryBreadcrumb(msg, 'log', 'log', context);
  consoleLog('log', message, context);
};

/**
 * Info - Informational logging
 * Adds breadcrumb to Sentry (no event, just context)
 */
const info = (message: string | Error, context?: LogContext): void => {
  const msg = normalizeMessage(message);
  addSentryBreadcrumb(msg, 'info', 'info', context);
  consoleLog('info', message, context);
};

/**
 * Debug - Debug level logging
 * Adds breadcrumb to Sentry (no event, just context)
 */
const debug = (message: string | Error, context?: LogContext): void => {
  const msg = normalizeMessage(message);
  addSentryBreadcrumb(msg, 'debug', 'debug', context);
  consoleLog('debug', message, context);
};

/**
 * Breadcrumb - Explicit breadcrumb for custom categories
 * Does not create Sentry event, only adds to context trail
 */
const breadcrumb = (message: string, category: string = 'debug', data?: LogContext): void => {
  addSentryBreadcrumb(message, category, 'info', data);
  if (IS_DEVELOPMENT) {
    console.log(`[Breadcrumb:${category}]`, message, data || '');
  }
};

// ============================================================================
// Normal Severity (Breadcrumbs Only - No Sentry Events)
// ============================================================================

/**
 * Warning - Log warning as breadcrumb
 * Use for non-critical warnings (invalid data, missing optional values, etc.)
 * Does NOT create Sentry event - only adds context for when errors occur
 *
 * @example
 * logger.warning('Empty array provided', { arrayName: 'stops' })
 * logger.warning('Invalid date format', { date: userInput })
 */
const warning = (message: string, context?: LogContext): void => {
  consoleLog('warn', message, context);
  addSentryBreadcrumb(message, 'warning', 'warning', context);
};

/**
 * Error - Log error as breadcrumb
 * Use for handled errors that don't break functionality (missing DOM element, API retry, etc.)
 * Does NOT create Sentry event - only adds context for when critical errors occur
 *
 * @example
 * logger.error('Element not found', { selector: '#modal' })
 * logger.error('API call failed, retrying', { endpoint: '/api/data' })
 */
const error = (message: string, context?: LogContext): void => {
  consoleLog('error', message, context);
  addSentryBreadcrumb(message, 'error', 'error', context);
};

// ============================================================================
// Capture Methods (Creates Sentry Events - Use Sparingly!)
// ============================================================================

/**
 * Capture Warning - Creates Sentry event for important warnings
 * Use ONLY for warnings that indicate potential system issues or data problems
 * that need investigation but don't break functionality
 *
 * Examples:
 * - Business logic configuration errors
 * - Missing required external service connections
 * - Data integrity issues that could lead to problems
 *
 * @example
 * logger.captureWarning('Payment gateway not configured', { enterpriseId })
 * logger.captureWarning('Required feature flag missing', { flagName })
 */
const captureWarning = (message: string, context?: LogContext): void => {
  consoleLog('warn', message, context);
  const normalizedContext = normalizeContext(context);
  Sentry.captureMessage(message, {
    level: 'warning',
    contexts: normalizedContext ? {
      extra: normalizedContext
    } : undefined
  });
};

/**
 * Capture Exception - Captures Error object with stack trace (creates Sentry event)
 * Use for actual Error objects from try-catch blocks
 * Handles unknown types by converting them to Error objects
 *
 * @example
 * try {
 *   await riskyOperation()
 * } catch (err) {
 *   logger.captureException(err, { operationContext })
 * }
 */
const captureException = (err: unknown, context?: LogContext): void => {
  // Convert unknown errors to Error objects
  const error = err instanceof Error ? err : new Error(String(err));
  if (IS_DEVELOPMENT) {
    console.error('Exception:', error, context);
  }
  const normalizedContext = normalizeContext(context);
  if (normalizedContext) {
    Sentry.setContext(EXCEPTION_CONTEXT_KEY, normalizedContext);
  }
  Sentry.captureException(error);
};

// ============================================================================
// User Tracking Functions
// ============================================================================

/**
 * Set user context for error tracking
 */
const setUser = (user: UserContext | null): void => {
  Sentry.setUser(user);
};

/**
 * Clear user context (e.g., on logout)
 */
const clearUser = (): void => {
  Sentry.setUser(null);
};

/**
 * Identify user - Maps payload to Sentry user context
 * Ensures id field exists for Sentry's primary identifier
 */
const identify = (payload: Record<string, string | number | boolean>): void => {
  // Extract id from common field names
  // Cast to string/number since id should never realistically be boolean
  const id = (payload.id || payload.userId || payload.accountId) as string | number | undefined;
  Sentry.setUser({
    ...payload,
    id
  });
};

// ============================================================================
// Advanced Sentry Functions
// ============================================================================

/**
 * Set custom context for all subsequent events
 */
const setContext = (key: string, context: LogContext | null): void => {
  Sentry.setContext(key, context ? normalizeContext(context) ?? null : null);
};

/**
 * Add tag for filtering in Sentry dashboard
 */
const setTag = (key: string, value: string): void => {
  Sentry.setTag(key, value);
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Main logger object - recommended way to access logging functions
 * Organized by severity and purpose for better discoverability
 */
export const logger = {
  // Breadcrumbs (no Sentry events, just context)
  log,
  info,
  debug,
  breadcrumb,
  // Normal severity (breadcrumbs only, no Sentry events)
  warning,
  // Backward compatibility with logger.warn
  warn: warning,
  error,
  // Capture methods (creates Sentry events - use sparingly!)
  captureWarning,
  captureException,
  // User tracking
  setUser,
  clearUser,
  identify,
  // Advanced Sentry
  setContext,
  setTag
};

// Standalone exports for direct imports
export { captureException, captureWarning };

// Legacy compatibility aliases for migration
export const addBreadcrumb = breadcrumb;
export const setUserContext = setUser;
export const clearUserContext = clearUser;
export { identify };
export default logger;